import * as React from 'react'

import { Colors, Dialog, DialogBody, DialogFooter, Button, Checkbox, Intent } from '@blueprintjs/core'

import { useSelector } from 'react-redux'
import Images from '.'
import Client from '../../client'
import config from '../../config'
import { UprightStyle } from '../../types'
import ImageHelpers from './Helpers'
import { PhotoEditor } from './PhotoEditor'
import { showSuccess } from '../../Toast'
import useRememberedState from '../../hooks/useRememberedState'
import { AppState } from '../../store'

interface Image {
  id?: number
  key: string
  url: string
  rotation: number
  rank?: number
  isEdited?: boolean
  oldKey?: string
  oldUrl?: string
}

interface PropsInterface {
  maxImages?: number
  isDisabled?: boolean
  disableUpload?: boolean
  disableEdit?: boolean
  lockImages?: boolean
  presignRoute?: any
  images: Image[]
  onChange: (images, cb?: () => void) => void
}

const style: UprightStyle = {
  watermarkDisclaimer: { marginTop: 5, marginBottom: 0, color: Colors.GRAY2 }
}

const RemoveConfirmationDialog: React.FC<{
  isOpen: boolean
  setIsOpen: (boolean) => any
  onConfirm: () => void
  dontAskAgainChecked: boolean
  toggleDontAskAgainChecked: () => any
}> = props => {
  const close = () => props.setIsOpen(!props.isOpen)

  return (
    <Dialog isOpen={props.isOpen} title="Confirm remove image" onClose={close}>
      <DialogBody>
        <h3>Are you sure you want to remove this image?</h3>
        <p>Images that are removed cannot be restored.</p>
        <Checkbox
          checked={props.dontAskAgainChecked}
          label="Don't ask me again"
          onChange={props.toggleDontAskAgainChecked}
        />
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <Button intent="none" minimal onClick={close}>
              Cancel
            </Button>
            <Button intent="danger" onClick={props.onConfirm}>
              Remove image
            </Button>
          </>
        }
      />
    </Dialog>
  )
}

const Uploader = (props: PropsInterface) => {
  const [selectedImageIndexToEdit, setSelectedImageIndexToEdit] = React.useState<number>(null)

  const setInnerImage = (index, cb) => {
    const { images } = props
    images[index] = cb(images[index])

    props.onChange(images)
  }

  const rotateImage = index => {
    if (!props.lockImages) {
      const image = props.images[index]
      const rotation = image.rotation + 90

      setInnerImage(index, img => ({ ...img, rotation }))
    }
  }

  const editImage = async index => {
    if (!props.lockImages) {
      setSelectedImageIndexToEdit(index)
    }
  }

  const uploadImage = async (index, image) => {
    const { size } = image

    setInnerImage(index, img => ({ ...img, loading: true, progress: 0, failed: false }))

    const presignFunction = props.presignRoute || Client.presignV5
    try {
      const presignRes = await presignFunction(image.type)
      await Client.uploadS3V2(presignRes.data.url, presignRes.data.fields, image.type, image, e => {
        setInnerImage(index, img => ({ ...img, progress: e.loaded / size }))
      })

      // spinner if things take longer than expected
      setTimeout(() => {
        setInnerImage(index, img => ({ ...img, progress: null }))
      }, 500)

      await ImageHelpers.preloadImage(presignRes.data.source_url)

      setInnerImage(index, () => ({
        key: presignRes.data.key,
        url: presignRes.data.source_url,
        loading: false,
        rotation: 0
      }))
    } catch (_error) {
      setInnerImage(index, img => ({ ...img, failed: true }))
    }
  }

  const onDrop = async images => {
    const imagesToUpload = ImageHelpers.onDrop(props.images, images, props.maxImages)
    props.onChange(imagesToUpload, async () => {
      let i = 0

      for (const img of imagesToUpload) {
        if (img.preview && !img.loading) {
          if (config.IS_IPAD) {
            await uploadImage(i, img)
          } else {
            uploadImage(i, img)
          }
        }
        i += 1
      }
    })
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (!props.lockImages) {
      props.onChange(ImageHelpers.onSortEnd(props.images, oldIndex, newIndex))
    }
  }

  const removeImage = index => {
    if (!props.lockImages) {
      props.onChange(ImageHelpers.onRemoveImageV2(props.images, index))
    }
  }

  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = React.useState(false)
  const [imageIndexBeingRemoved, setImageIndexBeingRemoved] = React.useState(null)

  const [dontAskAgainPref, setDontAskAgainPref] = useRememberedState('remove-image-dont-ask-me-again', true, false)
  const [dontAskAgainChecked, setDontAskAgainChecked] = React.useState(dontAskAgainPref)
  const toggleDontAskAgainChecked = () => {
    setDontAskAgainChecked(!dontAskAgainChecked)
  }

  const maybeConfirmRemoveImage = index => {
    analytics.track('Product Form Image X Button Clicked') // regardless of feature flag state

    if (!config.CONFIRM_IMAGE_REMOVAL()) {
      analytics.track('Product Form Image Removed - Confirmation FF Off')

      removeImage(index)
    } else if (dontAskAgainPref) {
      analytics.track('Product Form Image Removed - Dont Ask Me Again')

      // respect the user's choice, just remove it
      removeImage(index)
    } else {
      analytics.track('Product Form Remove Image Confirmation Dialog Displayed')

      setImageIndexBeingRemoved(index)
      setIsRemoveDialogOpen(true)
    }
  }

  const onConfirmRemoveImage = () => {
    if (dontAskAgainChecked) {
      setDontAskAgainPref(true)
    }

    analytics.track('Product Form Image Removed - Confirmed')

    removeImage(imageIndexBeingRemoved)
    setIsRemoveDialogOpen(false)
    showSuccess('Image removed', Intent.PRIMARY)
  }

  const watermarkImageSet = useSelector((s: AppState) => s.current.organization.settings.watermarkImageSet)
  const displayWatermarkDisclaimer = () => {
    const imagesPresent = props.images.length > 0
    return watermarkImageSet && imagesPresent
  }

  const handleExportedImage = async exportedImage => {
    const index = selectedImageIndexToEdit
    const { images } = props

    // We need to remove the previous version of the image but keep track of URL and Key if the edit is reverted.
    const previousURL = images[index].url
    const previousKey = images[index].key
    removeImage(index)

    setInnerImage(index, img => ({ ...img, loading: true, progress: 0, failed: false }))

    const split = exportedImage.split(',')
    const blob = ImageHelpers.b64toBlob(split[1], 'image/jpeg')
    const imageFile = ImageHelpers.blobToFile(blob, `exportedImage-${index}`)

    const { size } = imageFile

    const presignRes = await Client.presignV5('image/jpeg')
    await Client.uploadS3V2(presignRes.data.url, presignRes.data.fields, imageFile.type, imageFile, e => {
      setInnerImage(index, img => ({ ...img, progress: e.loaded / size }))
    })

    const newImage: Image = {
      rotation: 0,
      key: presignRes.data.key,
      url: presignRes.data.source_url,
      isEdited: true,
      oldUrl: previousURL,
      oldKey: previousKey
    }

    setInnerImage(index, () => newImage)
  }

  const revertImageEdit = index => {
    const image = props.images[index]
    const url = image.oldUrl
    const key = image.oldKey

    setInnerImage(index, img => ({ ...img, url, key, isEdited: false }))
  }

  const closeEditor = React.useCallback(() => {
    setSelectedImageIndexToEdit(null)
  }, [])

  return (
    <>
      {selectedImageIndexToEdit !== null && (
        <PhotoEditor
          onClose={closeEditor}
          imageURL={props.images[selectedImageIndexToEdit]?.url}
          onExport={handleExportedImage}
        />
      )}
      <Images
        maxImages={props.maxImages}
        images={props.images}
        onDrop={onDrop}
        onSortEnd={onSortEnd}
        removeImage={maybeConfirmRemoveImage}
        revertImageEdit={revertImageEdit}
        editImage={editImage}
        rotateImage={rotateImage}
        isDisabled={props.isDisabled}
        disableUpload={props.disableUpload}
        disableEdit={props.disableEdit}
      />
      <RemoveConfirmationDialog
        isOpen={isRemoveDialogOpen}
        setIsOpen={setIsRemoveDialogOpen}
        onConfirm={onConfirmRemoveImage}
        dontAskAgainChecked={dontAskAgainChecked}
        toggleDontAskAgainChecked={toggleDontAskAgainChecked}
      />
      {displayWatermarkDisclaimer() && (
        <p style={style.watermarkDisclaimer}>Watermarks will be added to images when uploaded to the channel</p>
      )}
    </>
  )
}

export default React.memo(Uploader)
